import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef } from "react";
import { getOnboardingStepStatus } from "../redux/slices/QuestionnaireSlice";
import { setActiveCategory } from "../redux/slices/dealCategorySlice";
import { getOrgDealCategories, logout } from "../redux/slices/AuthSlice";
import { GLOBAL_CONSTANTS } from "../constants/constants";
import { Spin } from "antd";
import {
  isSupplyChainCategory,
  isCategoryRestricted,
  getUnrestrictedDealCategories,
  pickAccessibleCategory,
} from "../helpers/dealCategoryProducts";

const ProtectedRoute = ({ children }) => {
  const { user, dealCategoriesStatus } = useSelector((state) => state.auth);
  const {
    onboardingStepStatus,
    onboardingStepStatusLoad,
    onboardingDealCategoryId,
  } = useSelector((state) => state.questionnaire);
  const { activeCategory } = useSelector((state) => state.dealCategory);
  const dispatch = useDispatch();
  const location = useLocation();
  const categoryRetryCount = useRef(0);

  const isForcePasswordPath = location.pathname === "/force-change-password";
  const mustChangePassword = Boolean(user?.mustChangePassword);

  useEffect(() => {
    if (!user?.orgId || mustChangePassword || isForcePasswordPath) {
      return;
    }
    if (!dealCategoriesStatus || dealCategoriesStatus === "idle") {
      categoryRetryCount.current = 0;
      dispatch(getOrgDealCategories());
      return;
    }
    if (dealCategoriesStatus === "failed" && categoryRetryCount.current < 2) {
      categoryRetryCount.current += 1;
      dispatch(getOrgDealCategories());
    }
  }, [
    dispatch,
    user?.orgId,
    dealCategoriesStatus,
    mustChangePassword,
    isForcePasswordPath,
  ]);

  const hasLoginCategories = (user?.dealCategories || []).length > 0;
  const categoryDataReady =
    dealCategoriesStatus === "succeeded" ||
    dealCategoriesStatus === undefined ||
    (dealCategoriesStatus === "failed" && hasLoginCategories);

  const availableCategories = user?.dealCategories || [];
  const validCategories = getUnrestrictedDealCategories(availableCategories);
  const currentInUser = availableCategories.find(
    (c) => c.dealCategoryId === activeCategory?.dealCategoryId,
  );
  const currentStatusId = currentInUser?.statusId || activeCategory?.statusId;
  const isCurrentRestricted = !activeCategory || isCategoryRestricted(currentStatusId);
  const shouldSwitchCategory = validCategories.length > 0 && isCurrentRestricted;
  const hasNoAvailableCategories =
    dealCategoriesStatus === "succeeded" && availableCategories.length === 0;
  const hasOnlyRestrictedCategories =
    dealCategoriesStatus === "succeeded" &&
    availableCategories.length > 0 &&
    validCategories.length === 0;

  useEffect(() => {
    if (!categoryDataReady || !user?.dealCategories?.length) return;

    const categories = user.dealCategories;
    const dealCategoryIdFromUrl = new URLSearchParams(location.search).get(
      "dealCategoryId",
    );

    if (dealCategoryIdFromUrl) {
      const fromUrl = categories.find(
        (c) => c.dealCategoryId === dealCategoryIdFromUrl,
      );
      if (
        fromUrl &&
        !isCategoryRestricted(fromUrl?.statusId) &&
        activeCategory?.dealCategoryId !== fromUrl.dealCategoryId
      ) {
        dispatch(setActiveCategory(fromUrl));
        return;
      }
    }

    const latest = categories.find(
      (c) => c.dealCategoryId === activeCategory?.dealCategoryId,
    );
    const latestStatusId = latest?.statusId || activeCategory?.statusId;
    const restricted = !activeCategory || isCategoryRestricted(latestStatusId);

    if (restricted) {
      const targetCategory = pickAccessibleCategory(
        categories,
        activeCategory?.dealCategoryId,
      );
      if (
        targetCategory &&
        activeCategory?.dealCategoryId !== targetCategory.dealCategoryId
      ) {
        dispatch(setActiveCategory(targetCategory));
      }
    } else if (latest && latest.statusId !== activeCategory?.statusId) {
      const STATUS_RANK = {
        [GLOBAL_CONSTANTS.STAT_KYC_PEN_ID]: 1,
        [GLOBAL_CONSTANTS.STAT_KYC_REVIEW_ID]: 2,
        [GLOBAL_CONSTANTS.STAT_KYC_VERIFIED_ID]: 3,
        [GLOBAL_CONSTANTS.STAT_ACTIVE_ID]: 4,
      };
      const userRank = STATUS_RANK[latest.statusId] || 0;
      const activeRank = STATUS_RANK[activeCategory?.statusId] || 0;
      if (userRank >= activeRank) {
        dispatch(setActiveCategory({ ...activeCategory, ...latest }));
      }
    }
  }, [user, activeCategory, dispatch, location.search, categoryDataReady]);

  useEffect(() => {
    if (
      mustChangePassword ||
      isForcePasswordPath ||
      !user ||
      !(hasNoAvailableCategories || hasOnlyRestrictedCategories)
    ) {
      return;
    }
    dispatch(logout(user.userId));
  }, [
    user,
    hasNoAvailableCategories,
    hasOnlyRestrictedCategories,
    dispatch,
    mustChangePassword,
    isForcePasswordPath,
  ]);

  useEffect(() => {
    if (
      mustChangePassword ||
      isForcePasswordPath ||
      !categoryDataReady ||
      validCategories.length === 0 ||
      !user?.orgId ||
      !activeCategory?.dealCategoryId ||
      isCurrentRestricted
    ) {
      return;
    }
    dispatch(
      getOnboardingStepStatus({
        orgId: user.orgId,
        dealCategoryId: activeCategory.dealCategoryId,
      }),
    );
  }, [
    categoryDataReady,
    validCategories.length,
    user?.orgId,
    dispatch,
    activeCategory?.dealCategoryId,
    isCurrentRestricted,
    mustChangePassword,
    isForcePasswordPath,
  ]);

  useEffect(() => {
    if (!onboardingStepStatus || !activeCategory || isCurrentRestricted) return;
    if (
      onboardingDealCategoryId &&
      onboardingDealCategoryId !== activeCategory.dealCategoryId
    ) {
      return;
    }
    if (isCategoryRestricted(currentInUser?.statusId)) return;

    let nextStatusId = null;
    if (onboardingStepStatus.Business_Active) {
      nextStatusId = GLOBAL_CONSTANTS.STAT_ACTIVE_ID;
    } else if (onboardingStepStatus.KYC_Verified) {
      nextStatusId = GLOBAL_CONSTANTS.STAT_KYC_VERIFIED_ID;
    } else if (onboardingStepStatus.KYC_Review) {
      nextStatusId = GLOBAL_CONSTANTS.STAT_KYC_REVIEW_ID;
    } else if (onboardingStepStatus.KYC_Pending) {
      nextStatusId = GLOBAL_CONSTANTS.STAT_KYC_PEN_ID;
    }

    if (!nextStatusId || activeCategory.statusId === nextStatusId) return;

    dispatch(
      setActiveCategory({
        ...activeCategory,
        statusId: nextStatusId,
      }),
    );

    if (user?.dealCategories?.length) {
      const updatedUser = {
        ...user,
        dealCategories: user.dealCategories.map((category) =>
          category.dealCategoryId === activeCategory.dealCategoryId
            ? { ...category, statusId: nextStatusId }
            : category,
        ),
      };
      localStorage.setItem("busUser", JSON.stringify(updatedUser));
    }
  }, [
    onboardingStepStatus,
    onboardingDealCategoryId,
    activeCategory,
    dispatch,
    user,
    isCurrentRestricted,
    currentInUser,
  ]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.mustChangePassword) {
    if (location.pathname === "/force-change-password") {
      return children;
    }
    return <Navigate to="/force-change-password" replace />;
  }

  if (hasNoAvailableCategories || hasOnlyRestrictedCategories) {
    return <Navigate to="/login" replace />;
  }

  if (
    dealCategoriesStatus === "idle" ||
    dealCategoriesStatus === "loading" ||
    (dealCategoriesStatus === "failed" && !hasLoginCategories)
  ) {
    return <Spin fullscreen />;
  }

  const staleOnboarding =
    Boolean(onboardingDealCategoryId) &&
    onboardingDealCategoryId !== activeCategory?.dealCategoryId;
  const waitingForOnboarding =
    staleOnboarding ||
    (!onboardingStepStatus &&
      onboardingStepStatusLoad !== "failed" &&
      onboardingStepStatusLoad !== "succeeded");

  if (shouldSwitchCategory || isCurrentRestricted || waitingForOnboarding) {
    return <Spin fullscreen />;
  }

  const isActive =
    (!staleOnboarding && onboardingStepStatus?.Business_Active === true) ||
    ((staleOnboarding || !onboardingStepStatus) &&
      currentStatusId === GLOBAL_CONSTANTS.STAT_ACTIVE_ID);

  const allowedPreActivePaths = [
    "/questionnaire",
    "/complete-kyc",
    "/sla",
    "/nda",
    "/profile",
    "/Profile",
  ];

  if (!isActive && !allowedPreActivePaths.includes(location.pathname)) {
    return <Navigate to="/questionnaire" replace />;
  }

  const isSupplierPath =
    location.pathname.startsWith("/suppliers") ||
    location.pathname.startsWith("/supplier");

  const currentDealCategoryId =
    activeCategory?.dealCategoryId || user?.dealCategories?.[0]?.dealCategoryId;

  if (isSupplierPath && !isSupplyChainCategory(currentDealCategoryId)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
